import React, { useEffect, useState } from 'react';
import { CheckCircle2, ExternalLink, Loader2, RefreshCw, Search, XCircle } from 'lucide-react';
import api from '../lib/api';

const GOOGLE_LINKS = [
  { key: 'search_console', title: 'Google Search Console', desc: 'See what people typed into Google before clicking your site. Free — sign in with a Google account and add goleicestershire.com as a property.' },
  { key: 'rich_results_test', title: 'Rich Results Test', desc: 'Checks your homepage FAQ schema is valid — pre-filled with your live site, just press "Test URL".' },
  { key: 'pagespeed', title: 'PageSpeed Insights', desc: 'Google\u2019s speed report for your live site — speed is an SEO ranking factor.' },
];

export default function AdminSeo() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try { const r = await api.get('/admin/seo/status'); setData(r.data); }
    catch { setData({ fetch_error: 'Status check failed — try again.' }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-seo-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label text-heritage mb-1">Search visibility</div>
          <h1 className="font-display text-2xl sm:text-3xl">SEO status</h1>
          <p className="text-sm text-muted mt-1 max-w-2xl">A live check of what Google sees on <strong>{data?.site || 'your live site'}</strong>. If anything is red, your preview already has the fix — press Deploy to push it live, then re-check.</p>
        </div>
        <button onClick={load} disabled={loading} data-testid="seo-recheck" className="inline-flex items-center gap-2 bg-charcoal text-base px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-60">
          {loading ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Re-check live site
        </button>
      </div>

      {data?.fetch_error && (
        <div className="bg-heritage/10 border border-heritage rounded-2xl p-4 text-heritage text-sm mb-6" data-testid="seo-fetch-error">{data.fetch_error}</div>
      )}

      {/* Live checks */}
      <div className="bg-white border border-charcoal/10 rounded-2xl overflow-hidden mb-8" data-testid="seo-checks">
        <div className="px-5 py-4 border-b border-charcoal/10 flex items-center justify-between">
          <div className="font-semibold text-sm">Live site check</div>
          {data && !data.fetch_error && (
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${data.all_ok ? 'bg-forest/10 text-forest' : 'bg-mustard/25 text-charcoal'}`} data-testid="seo-overall">
              {data.all_ok ? 'All good — Google sees the new SEO' : 'Needs a deploy — some items not live yet'}
            </span>
          )}
        </div>
        {!data && <div className="p-6 text-sm text-muted italic">Checking your live site…</div>}
        {(data?.checks || []).map((c) => (
          <div key={c.id} className="px-5 py-4 border-b border-charcoal/5 last:border-0 flex items-start gap-3" data-testid={`seo-check-${c.id}`}>
            {c.ok ? <CheckCircle2 size={18} className="text-forest shrink-0 mt-0.5"/> : <XCircle size={18} className="text-heritage shrink-0 mt-0.5"/>}
            <div className="min-w-0">
              <div className="font-semibold text-sm">{c.label}</div>
              <div className="text-xs text-muted mt-0.5 break-words">{c.ok ? c.found : <>Live site shows: <em>{c.found || 'nothing'}</em> · Expected: {c.expected}</>}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Expected titles */}
      <div className="bg-white border border-charcoal/10 rounded-2xl overflow-hidden mb-8" data-testid="seo-titles">
        <div className="px-5 py-4 border-b border-charcoal/10">
          <div className="font-semibold text-sm">What your pages tell Google</div>
          <div className="text-xs text-muted mt-0.5">These titles are written to capture people searching "visit Leicester" — the phrase your main competitor ranks for.</div>
        </div>
        {(data?.expected_titles || []).map((t) => (
          <div key={t.path} className="px-5 py-3 border-b border-charcoal/5 last:border-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <div className="text-xs font-bold uppercase tracking-wider text-muted w-24 shrink-0">{t.page}</div>
            <div className="text-sm text-sky truncate">{t.title}</div>
          </div>
        ))}
      </div>

      {/* Google tools */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="seo-google-links">
        {GOOGLE_LINKS.map((g) => (
          <a key={g.key} href={data?.links?.[g.key] || '#'} target="_blank" rel="noreferrer" data-testid={`seo-link-${g.key}`}
            className="bg-white border border-charcoal/10 rounded-2xl p-5 group hover:border-heritage hover:shadow-soft transition-all">
            <div className="flex items-center justify-between mb-2">
              <Search size={16} className="text-heritage"/>
              <ExternalLink size={14} className="text-muted group-hover:text-heritage"/>
            </div>
            <div className="font-semibold text-sm">{g.title}</div>
            <div className="text-xs text-muted mt-1 leading-relaxed">{g.desc}</div>
          </a>
        ))}
      </div>
    </div>
  );
}
